import { CoreType } from '@/interfaces/core.interface'
import { isValidFeature } from '@/utils/isValidFeature'
import { getCoordinatesFromQueryParams, getPropertyFromQuery } from '@/utils/query-param-utils'
import * as turf from '@turf/turf'

import mapboxgl from 'mapbox-gl'
import { useI18n } from 'vue-i18n'
import store from '@/store'

let map: mapboxgl.Map | undefined = undefined
let container: HTMLDivElement | undefined = undefined

export type ZoomOptions = {
  zoomLimits?: { minZoom: number; maxZoom: number }
  forceAnimation?: boolean
  pitchLimits?: { minPitch: number; maxPitch: number }
  zoomAround?: 'center'
  zoomTrigger?: 'contained' | 'intersect'
  zoomTreshold?: number
  pitchTreshold?: number
} & mapboxgl.MapOptions['fitBoundsOptions']

const defaultOptions: ZoomOptions = {
  pitch: 0,
  bearing: 0,
  padding: 0.1,
  zoomLimits: { minZoom: 4, maxZoom: 22 },
  pitchLimits: { minPitch: 0, maxPitch: 60 },
  forceAnimation: false,
  zoomTrigger: 'contained',
  zoomTreshold: 1,
  pitchTreshold: 5
}

const useMapbox = () => {
  if (!map) {
    const [m, c] = initialize()
    map = m
    container = c
    map?.on('load', () => {
      // @ts-expect-error - the store is not typed
      store.map = map
      store.commit('SET_CURRENT_ZOOM', getInitialZoom())

      let mapIsLoaded = false
      if (store.getters.allBuildings.length > 0) {
        store.commit('SET_MAP_IS_LOADED', true)
        mapIsLoaded = true
        console.log('Map fully loaded')
      }

      if (!mapIsLoaded) {
        // Set map as loaded when building source is set
        store.subscribe((mutation) => {
          if (mutation.type === 'SET_BUILDING_SOURCE') {
            store.commit('SET_MAP_IS_LOADED', true)
            console.log('Map fully loaded')
          }
        })
      }
    })
    map?.on('move', () => {
      const pitch = map?.getPitch()
      if (pitch) store.state.currentPitch = pitch
    })
  }

  // Used for debugging purposes and end-to-end tests
  // @ts-expect-error - this is not typed
  window.map = map

  return {
    map,
    container
  }
}

const zoomOptions: ZoomTriggerMatcher[] = [
  {
    entityTypes: ['CONSTRUCTION'],
    match: { currentView: 'planview' },
    options: {
      padding: 0.1,
      pitch: 0,
      zoomLimits: { minZoom: 16, maxZoom: 22 },
      pitchLimits: { minPitch: 0, maxPitch: 0 }
    }
  },
  {
    entityTypes: ['CONSTRUCTION'],
    match: { currentView: 'modelview' },
    options: { padding: 0.1, pitch: 45, zoomLimits: { minZoom: 16, maxZoom: 22 } }
  },
  {
    entityTypes: ['CONSTRUCTION'],
    match: { singleEntity: true },
    options: { padding: 0.4, pitch: 45 }
  },
  {
    entityTypes: ['CONSTRUCTION'],
    options: { padding: 0.2, pitch: 0 }
  },
  {
    entityTypes: ['REAL_ESTATE'],
    options: { padding: 0.2, pitch: 0 }
  },
  {
    entityTypes: ['COMPLEX'],
    options: { padding: 0.2, pitch: 45 }
  },
  {
    entityTypes: ['SPACE'],
    options: { padding: 0.4, pitch: 0, pitchLimits: { minPitch: 0, maxPitch: 0 } }
  }
]

function getInitialMinZoom(): number {
  // const isPlanviewShowing = getPropertyFromQuery(location.search, 'planview') === 'true'
  // const isModelviewShowing = getPropertyFromQuery(location.search, 'modelview') === 'true'
  return 4
}

function getInitialZoom(): number {
  const zoom = getPropertyFromQuery(location.search, 'zoom')
  return zoom ? parseInt(zoom) : 4
}

function getInitalPitch(): number {
  const pitch = getPropertyFromQuery(location.search, 'pitch')
  return pitch ? parseInt(pitch) : 0
}

function getInitalBearing(): number {
  const bearing = getPropertyFromQuery(location.search, 'bearing')
  return bearing ? parseInt(bearing) : 0
}

function getInitialCenter(): [number, number] | undefined {
  const { lat, lng } = getCoordinatesFromQueryParams(location.search)
  return lat && lng ? [lng, lat] : undefined
}

export const GATEWAY_HOST = window.appConfig.VITE_API_GW_BASE_URL
  ? new URL(window.appConfig.VITE_API_GW_BASE_URL).host
  : ''

function initialize(): [mapboxgl.Map, HTMLDivElement] {
  const container = document.createElement('div')
  container.id = 'mapContainer'
  container.className = 'map'
  container.style.width = '100%'
  container.style.height = '100%'

  mapboxgl.accessToken = window.appConfig.VITE_MAPBOX_ACCESS_TOKEN!
  const attributionControl = new mapboxgl.AttributionControl({
    compact: true
  })
  return [
    new mapboxgl.Map({
      antialias: true,
      attributionControl: false,
      center: getInitialCenter(),
      container: container,
      minZoom: getInitialMinZoom(),
      style: 'mapbox://styles/zynkadb/ckkz9rlkl1t2017s7khosquzc?optimize=true',
      zoom: getInitialZoom(),
      pitch: getInitalPitch(),
      bearing: getInitalBearing(),
      bearingSnap: 0,
      locale: getMapTranslations(),
      preserveDrawingBuffer: true,
      // @ts-expect-error - mapbox-gl is incorrectly typed (does not allow undefined as a return value). This follows the official documentation.
      transformRequest: (url, resourceType) => {
        if (resourceType === 'Tile' && url.includes(GATEWAY_HOST)) {
          return {
            url,
            headers: {
              Authorization: `Bearer ${localStorage.getItem('currentKeycloakToken')}`
            }
          }
        }
      }
    }).addControl(attributionControl, 'bottom-left'),
    container
  ]
}

function getMapTranslations() {
  const { t } = useI18n()
  return {
    'AttributionControl.ToggleAttribution': t('mapbox__toggle__attribution'),
    'AttributionControl.MapFeedback': t('mapbox__map__feedback'),
    'FullscreenControl.Enter': t('mapbox__enter'),
    'FullscreenControl.Exit': t('mapbox__exit'),
    'GeolocateControl.FindMyLocation': t('mapbox__find__my__location'),
    'GeolocateControl.LocationNotAvailable': t('mapbox__location__not_available'),
    'LogoControl.Title': t('mapbox__logo__control__title'),
    'NavigationControl.ResetBearing': t('mapbox__navigation__control__reset'),
    'NavigationControl.ZoomIn': t('mapbox__zoom__in'),
    'NavigationControl.ZoomOut': t('mapbox__zoom__out'),
    'ScaleControl.Feet': 'ft',
    'ScaleControl.Meters': 'm',
    'ScaleControl.Kilometers': 'km',
    'ScaleControl.Miles': 'mi',
    'ScaleControl.NauticalMiles': 'nm',
    'ScrollZoomBlocker.CtrlMessage': t('mapbox__scroll__zoom__blocker__ctrl__message'),
    'ScrollZoomBlocker.CmdMessage': t('mapbox__scroll__zoom__blocker__cmd__message'),
    'TouchPanBlocker.Message': t('mapbox__touch__pan__blocker__message')
  }
}

export function intersects(
  bbox1: [number, number, number, number],
  bbox2: [number, number, number, number]
): boolean {
  return bbox1[0] < bbox2[2] && bbox1[2] > bbox2[0] && bbox1[1] < bbox2[3] && bbox1[3] > bbox2[1]
}

export function within(
  bbox1: [number, number, number, number],
  bbox2: [number, number, number, number]
): boolean {
  return bbox1[0] > bbox2[0] && bbox1[1] > bbox2[1] && bbox1[2] < bbox2[2] && bbox1[3] < bbox2[3]
}

export function mapToBBox(map: mapboxgl.Map): [number, number, number, number] | undefined {
  if (!map) return
  const bounds = map.getBounds()
  if (!bounds) return
  return [bounds.getWest(), bounds.getSouth(), bounds.getEast(), bounds.getNorth()]
}

function disableUserInteractions(): void {
  map?.scrollZoom.disable()
  map?.boxZoom.disable()
  map?.doubleClickZoom.disable()
  map?.dragRotate.disable()
  map?.keyboard.disable()
  map?.touchZoomRotate.disable()
}

function enableUserInteractions(): void {
  map?.scrollZoom.enable()
  map?.boxZoom.enable()
  map?.doubleClickZoom.enable()
  map?.dragPan.enable()
  map?.dragRotate.enable()
  map?.keyboard.enable()
  map?.touchZoomRotate.enable()
}

function isContainedInView(featuresBBox: mapboxgl.LngLatBounds): boolean {
  if (!map) return false
  const mapBBox = map.getBounds()
  if (!mapBBox) return false
  const a = turf.bboxPolygon(turf.bbox(turf.lineString(mapBBox.toArray())))
  const b = turf.bboxPolygon(turf.bbox(turf.lineString(featuresBBox.toArray())))
  return turf.booleanContains(a, b)
}

function isInView(featuresBBox: mapboxgl.LngLatBounds): boolean {
  if (!map) return false
  const mapBBox = map.getBounds()
  if (!mapBBox) return false
  const a = turf.bboxPolygon(turf.bbox(turf.lineString(mapBBox.toArray())))
  const b = turf.bboxPolygon(turf.bbox(turf.lineString(featuresBBox.toArray())))
  return turf.booleanOverlap(a, b) || turf.booleanContains(a, b)
}

function getAllFeatures(): GeoJSON.Feature[] {
  const selectedLevels = store.state.levelsForSelectedBuilding?.['data']?.['features'] ?? []
  const selectedRooms = store.state.roomsForSelectedLevel?.['data']?.['features'] ?? []
  return store.getters.allRealEstates
    .concat(store.getters.allBuildings)
    .concat(selectedLevels)
    .concat(selectedRooms)
}

function getIdsForFeature(feature: GeoJSON.Feature): string[] {
  if (!feature.properties) return []

  const properties = feature.properties

  return ['id', 'complexId', 'realEstateId'].map((id) => properties[id]).filter((id) => id)
}

function findFeaturesByTypeAndId(entities: string[]): GeoJSON.Feature[] {
  const allFeatures = getAllFeatures()
  const features: GeoJSON.Feature[] = allFeatures.filter((feature) =>
    getIdsForFeature(feature).some((id) => entities.includes(id))
  )
  return features
}

type ZoomTriggerMatch = {
  currentView?: string
  previousView?: string
  singleEntity?: boolean
}

type ZoomTriggerMatcher = {
  entityTypes?: CoreType[]
  match?: ZoomTriggerMatch
  options: ZoomOptions
}

function getZoomOptions(
  entityTypes?: CoreType[],
  meta?: ZoomTriggerMatch
): mapboxgl.MapOptions['fitBoundsOptions'] {
  return {
    ...defaultOptions,
    ...(zoomOptions.find((option) => {
      return (
        (!option.entityTypes ||
          entityTypes?.some((entityType) => option.entityTypes?.includes(entityType))) &&
        (!option.match ||
          Object.entries(option.match).every(([key, value]) => meta?.[key] === value))
      )
    })?.options ?? {})
  }
}

export function zoomToEntities(
  entities: string[],
  animate: boolean = true,
  zoomOptions?: ZoomOptions
): void {
  const features = findFeaturesByTypeAndId(entities)
  if (features.length === 0) {
    console.warn('No valid features to zoom to')
    return
  }
  const entityTypes = [
    ...new Set(
      features
        .map((feature) => feature.properties?.featureType ?? feature.properties?.type)
        .filter((type) => type)
    )
  ] as CoreType[]
  const meta = {
    currentView: store.state.currentView,
    previousView: store.state.previousView,
    singleEntity: features.length === 1
  }
  const options = getZoomOptions(entityTypes, meta)
  zoomToFitBounds(features, { ...options, ...zoomOptions }, animate)
}

function setMapLimits(map: mapboxgl.Map, options: ZoomOptions) {
  const minZoom = options.zoomLimits?.minZoom ?? 4
  const maxZoom = options.zoomLimits?.maxZoom ?? 22

  const minPitch = options.pitchLimits?.minPitch ?? 0
  const maxPitch = options.pitchLimits?.maxPitch ?? 60

  map.setMinPitch(minPitch)
  map.setMaxPitch(maxPitch)
  map.setMaxZoom(maxZoom)
  map.setMinZoom(minZoom)
}

function unlockMapLimits(map: mapboxgl.Map, options: ZoomOptions) {
  const minZoom = options.zoomLimits?.minZoom ?? 4
  const maxZoom = options.zoomLimits?.maxZoom ?? 22
  const zoomDiff = maxZoom - minZoom
  const currentZoomDiff = map.getMaxZoom() - map.getMinZoom()

  const minPitch = options.pitchLimits?.minPitch ?? 0
  const maxPitch = options.pitchLimits?.maxPitch ?? 60
  const pitchDiff = maxPitch - minPitch
  const currentPitchDiff = map.getMaxPitch() - map.getMinPitch()

  if (pitchDiff > currentPitchDiff) {
    map.setMinPitch(minPitch)
    map.setMaxPitch(maxPitch)
  }

  if (zoomDiff > currentZoomDiff) {
    map.setMaxZoom(maxZoom)
    map.setMinZoom(minZoom)
  }
}

function calculateTargetZoom(
  map: mapboxgl.Map,
  targetBox: mapboxgl.LngLatBounds,
  containerBox: mapboxgl.LngLatBounds
) {
  const targetLonDelta = targetBox.getEast() - targetBox.getWest()
  const targetLatDelta = targetBox.getNorth() - targetBox.getSouth()
  const screenLonDelta = containerBox.getEast() - containerBox.getWest()
  const screenLatDelta = containerBox.getNorth() - containerBox.getSouth()

  const zoomLatMultiplier = screenLatDelta / targetLatDelta
  const zoomLonMultiplier = screenLonDelta / targetLonDelta

  const latZoom = Math.log2(zoomLatMultiplier)
  const lonZoom = Math.log2(zoomLonMultiplier)

  const targetZoom = map.getZoom() + Math.min(latZoom, lonZoom)

  return targetZoom
}

function zoomToFitBounds(
  features: GeoJSON.Feature[],
  options: ZoomOptions = defaultOptions,
  animate: boolean = true
): void {
  if (!map) return
  const validFeatures = features.filter(isValidFeature)
  if (validFeatures.length === 0) {
    console.warn('No valid features to zoom to')
    return
  }

  const targetBox = mapboxgl.LngLatBounds.convert([
    ...turf.bbox({ type: 'FeatureCollection', features: validFeatures }).values()
  ] as [number, number, number, number])

  const containerBox = map.getBounds()

  if (!containerBox) return

  const targetZoom = calculateTargetZoom(map, targetBox, containerBox)

  if (options.forceAnimation === false) {
    const zoomDifference = Math.abs((map.getZoom() ?? 0) - targetZoom)
    const pitchDifference = Math.abs((map.getPitch() ?? 0) - (options.pitch ?? 0))
    const isVisible =
      options.zoomTrigger === 'contained' ? isContainedInView(targetBox) : isInView(targetBox)
    console.debug('Force animation is disabled, checking if features are already in view port..')
    const isZoomedIn = zoomDifference < (options?.zoomTreshold ?? 1)
    const isPitchedIn = pitchDifference < (options?.pitchTreshold ?? 5)

    if (isVisible && isZoomedIn && isPitchedIn) {
      console.debug('Features are visible on map, returning without animating..')
      return
    }
    console.debug('Features are not visible on map, animating..')
  }

  const minDuration = 600
  const maxDuration = 1800
  const zoomFactor = 0.4
  const zoomDelta = Math.abs((map.getZoom() ?? 0) - targetZoom)
  const animationDuration = Math.max(minDuration, maxDuration - zoomDelta * zoomFactor)

  const width = window.innerWidth
  const height = window.innerHeight
  const minSize = Math.min(width, height)
  const padding = options.padding ?? 0.1

  if (!animate) {
    setMapLimits(map, options)
    console.debug('Animating is disabled, zooming without animation..')
    return
  }

  disableUserInteractions()

  unlockMapLimits(map, options)

  const newPadding =
    typeof padding === 'number'
      ? {
          top: Math.max(padding * minSize - 64, 0),
          bottom: padding * minSize,
          left: padding * minSize,
          right: padding * minSize
        }
      : {
          top: padding.top! * minSize,
          bottom: padding.bottom! * minSize,
          left: padding.left! * minSize,
          right: padding.right! * minSize
        }

  try {
    map.fitBounds(targetBox, {
      easing: (t) => t * (2 - t),
      animate: options.animate,
      duration: animationDuration,
      maxZoom: targetZoom,
      pitch: options.pitch ?? 0,
      padding: newPadding
    })
  } catch (error) {
    console.error('Error while fitting bounds', error)
  }

  map.once('moveend', () => {
    enableUserInteractions()
    setMapLimits(map!, options)
  })
}

export default useMapbox
